import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: "website" | "article";
  ogImage?: string;
}

const HREFLANG_MAP: Record<string, string> = {
  es: "es",
  en: "en",
  ca: "ca",
  pt: "pt",
  fr: "fr",
  it: "it",
  de: "de",
  ru: "ru",
};

const DEFAULT_OG_IMAGE = "https://chibikokoro.es/chibi-logo.webp";

const SEOHead = ({ title, description, canonical, ogType, ogImage }: SEOHeadProps) => {
  const { lang } = useLanguage();

  useEffect(() => {
    document.title = title;
    document.documentElement.lang = HREFLANG_MAP[lang] || "es";

    const resolvedType = ogType ?? (canonical && canonical.includes("/blog/") ? "article" : "website");
    const resolvedImage = ogImage ?? DEFAULT_OG_IMAGE;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", resolvedType, "property");
    setMeta("og:image", resolvedImage, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", resolvedImage);
    setMeta("twitter:card", "summary_large_image");

    if (canonical) {
      setMeta("og:url", canonical, "property");

      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonical;

      // Set hreflang x-default
      const hreflangId = "hreflang-x-default";
      let hreflangLink = document.getElementById(hreflangId) as HTMLLinkElement;
      if (!hreflangLink) {
        hreflangLink = document.createElement("link");
        hreflangLink.id = hreflangId;
        hreflangLink.rel = "alternate";
        hreflangLink.hreflang = "x-default";
        document.head.appendChild(hreflangLink);
      }
      hreflangLink.href = canonical;

      // Set hreflang for current language
      const hreflangLangId = "hreflang-current";
      let hreflangLangLink = document.getElementById(hreflangLangId) as HTMLLinkElement;
      if (!hreflangLangLink) {
        hreflangLangLink = document.createElement("link");
        hreflangLangLink.id = hreflangLangId;
        hreflangLangLink.rel = "alternate";
        document.head.appendChild(hreflangLangLink);
      }
      hreflangLangLink.hreflang = HREFLANG_MAP[lang] || "es";
      hreflangLangLink.href = canonical;
    }
  }, [title, description, canonical, lang, ogType, ogImage]);

  return null;
};

export default SEOHead;
